import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, CheckCircle, FileText, AlertCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: "drivers_license", label: "Driver's License", required: true },
  { key: "ssn_itin", label: "Social Security or ITIN", required: true },
  { key: "car_insurance", label: "Car Insurance", required: true },
];

const InspectorUploadPage = () => {
  const [token, setToken] = useState("");
  const [inspectorInfo, setInspectorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState({});
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/inspector-upload\/([a-zA-Z0-9-]+)/);
    if (match) setToken(match[1]);
    else setError("Invalid upload link");
  }, []);

  const fetchInfo = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/inspector-upload/${token}`);
      setInspectorInfo(res.data);
    } catch {
      setError("Invalid or expired upload link. Please contact your administrator.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchInfo(); }, [token, fetchInfo]);

  const handleFileChange = (docType, e) => {
    const file = e.target.files?.[0];
    if (file) { setFiles(prev => ({ ...prev, [docType]: file })); setValidationError(null); }
  };

  const removeFile = (docType) => {
    setFiles(prev => { const n = { ...prev }; delete n[docType]; return n; });
  };

  const handleSubmit = async () => {
    // Validate all required docs are uploaded
    const missing = DOC_TYPES.filter(d => d.required && !files[d.key]);
    if (missing.length > 0) {
      setValidationError(`Please upload all required documents: ${missing.map(m => m.label).join(', ')}`);
      return;
    }
    setValidationError(null);
    setUploading(true);
    setSuccess(false);
    try {
      const fd = new FormData();
      const docTypes = [];
      for (const [docType, file] of Object.entries(files)) {
        fd.append("files", file);
        docTypes.push(docType);
      }
      fd.append("doc_types", docTypes.join(","));
      fd.append("notes", notes);
      const res = await axios.post(`${API}/inspector-upload/${token}/files`, fd);
      setUploadResult(res.data);
      setSuccess(true);
      setFiles({});
      setNotes("");
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D1A054]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <img src="/aarifi-logo.png" alt="AARIFI" className="h-16 mx-auto mb-4 object-contain" />
          <AlertCircle className="h-10 w-10 text-[#C07070] mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-800 mb-2">Upload Link Error</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center" data-testid="upload-success">
          <img src="/aarifi-logo.png" alt="AARIFI" className="h-16 mx-auto mb-4 object-contain" />
          <CheckCircle className="h-10 w-10 text-[#6B8F62] mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-800 mb-2">Documents Uploaded Successfully</h1>
          <p className="text-sm text-gray-500 mb-4">{uploadResult?.uploaded} file(s) uploaded for {uploadResult?.inspector}</p>
          <p className="text-xs text-gray-400">Your documents are being reviewed. You will be contacted with next steps.</p>
        </div>
      </div>
    );
  }

  const fileCount = Object.keys(files).length;
  const requiredCount = DOC_TYPES.filter(d => d.required).length;
  const requiredUploaded = DOC_TYPES.filter(d => d.required && files[d.key]).length;
  const allRequiredDone = requiredUploaded === requiredCount;

  return (
    <div className="min-h-screen bg-[#F5F0E8] py-8 px-4" data-testid="inspector-upload-page">
      <div className="max-w-lg mx-auto">
        {/* AARIFI Logo + Header */}
        <div className="text-center mb-6">
          <img src="/aarifi-logo.png" alt="AARIFI Services LLC" className="h-20 mx-auto mb-3 object-contain" data-testid="aarifi-logo" />
          <h1 className="text-xl font-bold text-gray-800">Inspector Document Upload</h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload your documents for <span className="font-semibold text-[#2F5D8A]">{inspectorInfo?.inspector_name}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">All fields marked with * are required</p>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-600 font-medium" data-testid="validation-error">
            {validationError}
          </div>
        )}

        {/* Document Upload Cards */}
        <div className="space-y-3">
          {DOC_TYPES.map(dt => {
            const hasFile = files[dt.key];
            return (
              <div key={dt.key} className={`bg-white rounded-lg border-2 p-4 transition-colors ${hasFile ? "border-[#6B8F62]/40 bg-[#6B8F62]/5" : "border-gray-200"}`}
                data-testid={`doc-upload-${dt.key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {hasFile ? <CheckCircle className="h-4 w-4 text-[#6B8F62]" /> : <FileText className="h-4 w-4 text-gray-400" />}
                    <span className="text-sm font-medium text-gray-800">
                      {dt.label} {dt.required && <span className="text-red-400">*</span>}
                    </span>
                  </div>
                  {hasFile ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6B8F62] font-medium truncate max-w-[120px]">{hasFile.name}</span>
                      <button onClick={() => removeFile(dt.key)} className="p-1 hover:bg-gray-100 rounded"><X className="h-3 w-3 text-gray-400" /></button>
                    </div>
                  ) : (
                    <label className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-medium rounded-lg cursor-pointer transition-colors">
                      Select File
                      <input type="file" className="hidden" onChange={e => handleFileChange(dt.key, e)} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid={`file-input-${dt.key}`} />
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress indicator */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#6B8F62] rounded-full transition-all duration-300" style={{ width: `${(requiredUploaded / requiredCount) * 100}%` }} />
          </div>
          <span className="text-xs text-gray-500 font-medium">{requiredUploaded}/{requiredCount} required</span>
        </div>

        {/* Notes */}
        <div className="mt-4">
          <label className="text-xs text-gray-500 font-medium">Notes / Comments (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..."
            className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#D1A054] resize-none bg-white"
            rows={2} data-testid="upload-notes" />
        </div>

        {/* Submit */}
        <button onClick={handleSubmit} disabled={!allRequiredDone || uploading}
          className="w-full mt-4 py-3.5 bg-[#D1A054] hover:bg-[#B08A3E] text-white font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-sm"
          data-testid="submit-upload-btn">
          <Upload className="h-4 w-4" />
          {uploading ? "Uploading..." : allRequiredDone ? `Submit ${fileCount} Documents` : `Upload All Required Documents`}
        </button>

        <p className="text-center text-[10px] text-gray-400 mt-6">AARIFI Services LLC — Secure Document Upload</p>
      </div>
    </div>
  );
};

export default InspectorUploadPage;
